// ABOUTME: SPEC-74 — the logo soft reset as one hook: `reset()` for the wordmark, `addConnectParams()` for the
// ABOUTME: WebSocket connect effect. App.js holds two call sites; the policy and the debounce live here.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { flushAgentAudio } from './agentAudioFlush';
import { resetSpeechClock } from './speechClock';
import { transcriptStore } from './transcriptStore';
import { SOFT_RESET_DEBOUNCE_MS, applySoftResetParams, performSoftReset } from './softReset';

/**
 * The agent stops talking NOW and the abandoned turn's decorations go with it — the same set of resets
 * `useManualStop.flushAndRest` makes, because the same thing is true: the rest of that answer will never
 * be heard. `audio` is App's own refs and setters, passed straight through (no App.js knowledge here).
 */
function silence(audio) {
  flushAgentAudio({
    audioContext: audio.audioContext,
    activeAudioSourcesRef: audio.activeAudioSourcesRef,
    nextStartTime: audio.nextStartTime,
    speakingTimeoutRef: audio.speakingTimeoutRef,
    resetSpeechClock,
  });
  if (audio.isAgentSpeakingRef) audio.isAgentSpeakingRef.current = false;
  audio.setIsAgentSpeaking?.(false);
  audio.setIsUserSpeaking?.(false);
  audio.setAgentState?.('listening');
  audio.setLiveAgentText?.('');
  audio.setHighlightedFeatures?.([]);
  audio.highlightedThisTurnRef?.current?.clear?.();
  audio.setActiveLoreImage?.(null);
  transcriptStore.reset();
}

/**
 * @param {object} p
 * @param {boolean}  p.enabled   tenant flag `feature_flags.logo_soft_reset` (Ophion); off → nothing is offered
 * @param {boolean}  [p.experienceStarted] false once the experience ends (logout, a 1008 close, the OTP gate): the
 *   announcement is owed to a visit that no longer exists, so the marker is dropped and the next entry is ordinary
 * @param {boolean}  [p.blocked] true while a film, the waitlist form or the checkout overlay owns the screen:
 *   a payment in progress must never be silently discarded, and the film has its own skip control
 * @param {object}   p.deps      App's collaborators (see performSoftReset) plus `audio` (see `silence`)
 * @param {Function} [p.now]     injectable clock for the debounce tests
 * @returns {{ available: boolean, reset: Function, addConnectParams: Function }}
 */
export function useLogoSoftReset({ enabled, blocked = false, experienceStarted = true, deps, now = Date.now }) {
  const stateRef = useRef({ pending: null });   // { prevSid } until the new sid has opened once
  const lastAtRef = useRef(null);
  const depsRef = useRef(deps);
  depsRef.current = deps;

  useEffect(() => { if (!experienceStarted) stateRef.current.pending = null; }, [experienceStarted]);

  const reset = useCallback(() => {
    const t = now();
    if (lastAtRef.current !== null && t - lastAtRef.current < SOFT_RESET_DEBOUNCE_MS) {
      console.log('[SPEC-74][RESET] outcome=ignored why=debounce');
      return false;
    }
    lastAtRef.current = t;
    const d = depsRef.current;
    performSoftReset({
      ...d,
      state: stateRef.current,
      silenceAgent: () => silence(d.audio || {}),
    });
    return true;
  }, [now]);

  const addConnectParams = useCallback((params, kind) => {
    if (enabled) applySoftResetParams(params, stateRef.current, kind);
  }, [enabled]);

  const available = Boolean(enabled) && !blocked;
  return useMemo(() => ({ available, reset, addConnectParams }), [available, reset, addConnectParams]);
}

export default useLogoSoftReset;
